import { Injectable } from '@angular/core';

/**
 * 运行时桥服务 - 提供脚本访问控件的能力
 * 控件可以注册自身，脚本可以通过 scriptId 访问控件的属性和方法
 */
@Injectable({
    providedIn: 'root'
})
export class RuntimeBridgeService {
    // 存储所有注册的控件实例
    private components: Map<string, ComponentBridge> = new Map();

    constructor() {
        // 挂载到全局 window 对象，让脚本能访问
        this.exposeToWindow();
    }

    /**
     * 注册控件组件
     * @param scriptId 控件的脚本ID（用于脚本访问）
     * @param bridge 控件桥接对象
     */
    registerComponent(scriptId: string, bridge: ComponentBridge): void {
        if (!scriptId) {
            console.warn('[RuntimeBridge] 无法注册组件: scriptId 为空');
            return;
        }
        this.components.set(scriptId, bridge);
        console.log(`[RuntimeBridge] 注册组件: ${scriptId}`, bridge);
    }

    /**
     * 注销控件组件
     * @param scriptId 控件的脚本ID
     */
    unregisterComponent(scriptId: string): void {
        if (this.components.has(scriptId)) {
            this.components.delete(scriptId);
            console.log(`[RuntimeBridge] 注销组件: ${scriptId}`);
        }
    }

    /**
     * 获取控件的当前值
     * @param scriptId 控件的脚本ID
     * @returns 控件的当前值
     */
    getValue(scriptId: string): any {
        const component = this.components.get(scriptId);
        if (component) {
            return component.getValue();
        }
        console.warn(`[RuntimeBridge] 找不到组件: ${scriptId}`);
        return undefined;
    }

    /**
     * 设置控件的值
     * @param scriptId 控件的脚本ID
     * @param value 要设置的值
     */
    setValue(scriptId: string, value: any): void {
        const component = this.components.get(scriptId);
        if (component) {
            component.setValue(value);
        } else {
            console.warn(`[RuntimeBridge] 找不到组件: ${scriptId}`);
        }
    }

    /**
     * 获取控件的所有属性
     * @param scriptId 控件的脚本ID
     * @returns 控件的属性对象
     */
    getProperties(scriptId: string): any {
        const component = this.components.get(scriptId);
        if (component) {
            return component.getProperties();
        }
        console.warn(`[RuntimeBridge] 找不到组件: ${scriptId}`);
        return {};
    }

    /**
     * 设置控件的属性
     * @param scriptId 控件的脚本ID
     * @param properties 要设置的属性对象
     */
    setProperties(scriptId: string, properties: any): void {
        const component = this.components.get(scriptId);
        if (component) {
            component.setProperties(properties);
        } else {
            console.warn(`[RuntimeBridge] 找不到组件: ${scriptId}`);
        }
    }

    /**
     * 获取控件的 DOM 元素
     * @param scriptId 控件的脚本ID
     * @returns DOM 元素
     */
    getElement(scriptId: string): HTMLElement | null {
        const component = this.components.get(scriptId);
        if (component) {
            return component.getElement();
        }
        console.warn(`[RuntimeBridge] 找不到组件: ${scriptId}`);
        return null;
    }

    /**
     * 调用控件的方法
     * @param scriptId 控件的脚本ID
     * @param methodName 方法名
     * @param args 方法参数
     * @returns 方法返回值
     */
    callMethod(scriptId: string, methodName: string, ...args: any[]): any {
        const component = this.components.get(scriptId);
        if (component && component.callMethod) {
            return component.callMethod(methodName, ...args);
        }
        console.warn(`[RuntimeBridge] 找不到组件或方法: ${scriptId}.${methodName}`);
        return undefined;
    }

    /**
     * 获取所有已注册的组件ID列表
     * @returns 组件ID数组
     */
    getAllComponentIds(): string[] {
        return Array.from(this.components.keys());
    }

    /**
     * 检查组件是否已注册
     * @param scriptId 控件的脚本ID
     * @returns 是否已注册
     */
    hasComponent(scriptId: string): boolean {
        return this.components.has(scriptId);
    }

    /**
     * 将运行时桥暴露到全局 window 对象
     */
    private exposeToWindow(): void {
        if (typeof window !== 'undefined') {
            (window as any).FuxaRuntime = {
                // 获取控件值
                getValue: (scriptId: string) => this.getValue(scriptId),

                // 设置控件值
                setValue: (scriptId: string, value: any) => this.setValue(scriptId, value),

                // 获取控件属性
                getProperties: (scriptId: string) => this.getProperties(scriptId),

                // 设置控件属性
                setProperties: (scriptId: string, properties: any) => this.setProperties(scriptId, properties),

                // 获取控件DOM元素
                getElement: (scriptId: string) => this.getElement(scriptId),

                // 调用控件方法
                callMethod: (scriptId: string, methodName: string, ...args: any[]) =>
                    this.callMethod(scriptId, methodName, ...args),

                // 获取所有组件ID
                getAllComponents: () => this.getAllComponentIds(),

                // 检查组件是否存在
                hasComponent: (scriptId: string) => this.hasComponent(scriptId)
            };
            console.log('[RuntimeBridge] FuxaRuntime 已挂载到 window 对象');
        }
    }
}

/**
 * 组件桥接接口
 * 控件组件需要实现此接口来注册到运行时桥
 */
export interface ComponentBridge {
    /**
     * 获取控件当前值
     */
    getValue(): any;

    /**
     * 设置控件值
     * @param value 要设置的值
     */
    setValue(value: any): void;

    /**
     * 获取控件所有属性
     */
    getProperties(): any;

    /**
     * 设置控件属性
     * @param properties 要设置的属性对象
     */
    setProperties(properties: any): void;

    /**
     * 获取控件的 DOM 元素
     */
    getElement(): HTMLElement | null;

    /**
     * 调用控件的方法（可选）
     * @param methodName 方法名
     * @param args 方法参数
     */
    callMethod?(methodName: string, ...args: any[]): any;
}
